import React from 'react';
import { useRouter } from 'next/router';
import useI18n from 'helpers/hooks/useI18n';
import { ImageHelpers } from 'helpers/imageHelpers';
import { getAppropriateSkuName, getRedBadgePromoMessage, showPreBackOrderMessage } from 'helpers/productHelpers';
import { EAddToCartStatus, TNikonVariant } from 'types/TNikonProduct';
import { Props } from './interfaces';
import styles from './product-card.module.scss';
import { IMAGE_SOURCE } from '../../../../../../../../helpers/constants/imageSources';
import { CurrencyHelpers } from '../../../../../../../../helpers/currencyHelpers';
import { useFormat } from '../../../../../../../../helpers/hooks/useFormat';
import { getLocalizationInfo } from '../../../../../../../../project.config';
import { TLocalizationInfo } from '../../../../../../../../types/TLocalizationInfo';

const ProductCard = ({ item, isShowBackOrPreorderMessage, NikonCareComponent }: Props) => {
  const router = useRouter();
  const imageUnavailable = IMAGE_SOURCE.getUnavailableImage(router.locale);
  const image = item?.variant?.images?.[0] || imageUnavailable;
  const locale: TLocalizationInfo = getLocalizationInfo(router.locale);
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { currency } = useI18n();

  if (!item) {
    return null;
  }

  const productName = getAppropriateSkuName(item.name || '', item.variant);
  const { promoMessage } = getRedBadgePromoMessage(item, formatCartMessage, currency);

  return (
    <div className={styles['product-card']}>
      <a href={item?._url} title={item?.name}>
        <div className={styles['photo-container']}>
          <img
            src={ImageHelpers.modifiedCTImageUrl(image, 'medium')}
            alt={`${productName}_image`}
            loading="lazy"
            onError={({ currentTarget }) => {
              currentTarget.src = imageUnavailable;
            }}
          />
        </div>
        <div>
          <div className={styles['data-container']}>
            <div className={styles['content-container']}>
              <h3>{productName}</h3>
              <p>
                {formatCartMessage({ id: 'product.number', defaultMessage: 'Product Number:' })} {item?.variant?.sku}
              </p>
              <p>
                {formatCartMessage({ id: 'product.count', defaultMessage: 'QTY:' })} {item?.count}
              </p>
              {item &&
                isShowBackOrPreorderMessage &&
                showPreBackOrderMessage({
                  variant: item.variant as TNikonVariant,
                  count: item.count || 0,
                  status: EAddToCartStatus.PREORDER,
                  locale: locale.appLocale,
                }) && (
                  <p className={styles.productPreBackOrder}>
                    {formatCartMessage({
                      id: 'pre.order',
                      defaultMessage: 'THIS ITEM IS ON PREORDER.',
                    })}
                  </p>
                )}
              {item &&
                isShowBackOrPreorderMessage &&
                showPreBackOrderMessage({
                  variant: item.variant as TNikonVariant,
                  count: item.count || 0,
                  status: EAddToCartStatus.BACKORDER,
                  locale: locale.appLocale,
                }) && (
                  <p className={styles.productPreBackOrder}>
                    {formatCartMessage({
                      id: 'back.order',
                      defaultMessage: 'THIS ITEM IS ON BACKORDER.',
                    })}
                  </p>
                )}
            </div>
            <div>
              <div className={styles['with-discounts-price']}>
                <span data-testid="old-price" className={styles.oldPrice}>
                  {(Boolean(item?.discounts?.length) ||
                    item?.discountedPrice ||
                    !!item?.custom?.fields?.proratedPromoAmount ||
                    item.variant?.price?.custom?.fields?.basePrice) &&
                    (item.variant?.price?.custom?.fields?.basePrice?.centAmount
                      ? CurrencyHelpers.formatForCurrency(
                          CurrencyHelpers.multiplyCurrency(item.variant.price.custom.fields.basePrice, item.count) ?? 0,
                          router.locale,
                        )
                      : CurrencyHelpers.formatForCurrency(
                          CurrencyHelpers.multiplyCurrency(item?.price, item?.count) ?? 0,
                          router.locale,
                        ))}
                </span>

                <span data-testid="price" className={styles.currentPrice}>
                  {item?.totalPrice?.centAmount && item.count && item?.totalPriceWithProration
                    ? CurrencyHelpers.formatForCurrency(item?.totalPriceWithProration, locale.appLocale, 'USD', 2)
                    : item?.totalPrice &&
                      CurrencyHelpers.formatForCurrency(item?.totalPrice, locale.appLocale, 'USD', 2)}{' '}
                </span>

                {(item?.custom?.fields?.savedAmount || 0) > 0 && (
                  <span data-testid="save-money" className={styles['save-money']}>
                    {formatCartMessage({ id: `cartItem.discount.${item?.lineItemId}`, defaultMessage: 'You Save' })}{' '}
                    {CurrencyHelpers.formatForCurrency(
                      item?.custom?.fields?.savedAmount ?? 0,
                      locale.appLocale,
                      'USD',
                      2,
                    )}
                  </span>
                )}
                <div>
                  {promoMessage && (
                    <span data-testid="promo" className={styles.promo}>
                      {promoMessage}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          {!!NikonCareComponent && <NikonCareComponent />}
        </div>
      </a>
    </div>
  );
};

export default ProductCard;
